import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Button, Chip, Paper, Typography } from '@mui/material'
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridValueGetterParams } from '@mui/x-data-grid'
import HomeIcon from '@mui/icons-material/Home'
import Refresh from '@mui/icons-material/Refresh'
import StartOutlined from '@mui/icons-material/StartOutlined'
import AddIcon from '@mui/icons-material/Add'

import {
  useGetJournalReconPeriodsQuery
} from '../../../../api-services/financial/journalReconApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { formatDateStringAsApiFormat } from '../../../../utility/formatters/DateFormatter'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import OpenJournalReconPeriodDialog from './OpenJournalReconPeriodDialog'

const JournalReconPeriodsPage: React.FC = () => {
  const history = useHistory()
  const [openPeriodDialogOpen, setOpenPeriodDialogOpen] = useState<boolean>(false)

  const getPeriodsApiResult = useGetJournalReconPeriodsQuery()

  const goTo = (location: string): void => { history.push(location) }

  const handleViewPeriod = (periodStartAt: string): void => {
    goTo(`/app/finance-area/journal-recons/${periodStartAt}`)
  }

  const columns: GridColDef[] = [
    {
      field: 'periodStartAt',
      headerName: 'Period Start',
      width: 150,
      valueGetter: (params: GridValueGetterParams) => formatDateStringAsApiFormat(params.row.periodStartAt)
    },
    {
      field: 'periodEndAt',
      headerName: 'Period End',
      width: 150,
      valueGetter: (params: GridValueGetterParams) => formatDateStringAsApiFormat(params.row.periodEndAt)
    },
    {
      field: 'open',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        params.row.open
          ? <Chip label='Open' color='warning' size='small' />
          : <Chip label='Closed' color='success' size='small' />
      )
    },
    {
      field: 'view',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button onClick={() => { handleViewPeriod(params.row.periodStartAt) }} type='button' variant='text'>
          <StartOutlined />
        </Button>
      )
    }
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Monthly Journal Reconciliation</Typography>
        <Button variant={'text'} type='button' onClick={() => { goTo('/app/finance-area') }}>
          <HomeIcon style={{ color: '#444' }} />
        </Button>
      </Box>

      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pr: 4, pt: 2 }}>
          <Button
            variant={'contained'}
            color={'primary'}
            startIcon={<AddIcon />}
            onClick={() => { setOpenPeriodDialogOpen(true) }}
          >
            Open New Period
          </Button>
          <Button variant={'contained'} type='button' onClick={async () => { await getPeriodsApiResult.refetch() }}>
            <Refresh />
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          <h3>Journal Periods</h3>
          { getPeriodsApiResult.isLoading && <LoadingComponent /> }
          { getPeriodsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getPeriodsApiResult.error)} /> }
          { getPeriodsApiResult.isSuccess && (
            <DataGrid
              getRowId={(row) => row.periodStartAt}
              rows={getPeriodsApiResult.data}
              columns={columns}
              autoHeight={true}
              disableSelectionOnClick

              pagination
              pageSize={15}
              rowsPerPageOptions={[15, 25, 50]}
            />
          )}
        </Box>
      </Paper>

      { openPeriodDialogOpen && (
        <OpenJournalReconPeriodDialog
          onCloseDialog={() => { setOpenPeriodDialogOpen(false) }}
          onSuccess={(periodStartAt: string) => {
            setOpenPeriodDialogOpen(false)
            handleViewPeriod(periodStartAt)
          }}
        />
      )}
    </>
  )
}

export default JournalReconPeriodsPage
